import { HighwaySectionDto, ZoneType, CityDto, ZoneDto } from '@interfaces/types';
import { useMobilityHubPrivate } from '@content-api/provider';
import { useQuery } from '@tanstack/react-query';
import { DayOfWeek } from '@interfaces/models';
import { useMemo } from 'react';

const CACHE_TIME = 1000 * 60 * 60; // 1 hour

export const useGetCompanyPolicyData = () => {
  const { getCities, getZones } = useMobilityHubPrivate();

  const { data: cities, isLoading: isCitiesLoading } = useQuery({
    queryKey: ['cities'],
    queryFn: () => getCities(),
    staleTime: CACHE_TIME,
  });

  const { data: zones, isLoading: isZonesLoading } = useQuery({
    queryKey: ['zones'],
    queryFn: () => getZones(),
    staleTime: CACHE_TIME,
  });

  const mappedCities: CityDto[] = useMemo(() => {
    return (
      cities?.data.map((city) => ({
        code: city.code,
        name: city.name,
        zones: city.zones.map((zone) => ({
          code: zone.code,
          name: zone.name,
          type: zone.serviceType as unknown as ZoneType,
          cityCode: city.code,
        })),
      })) || []
    );
  }, [cities]);

  const mappedZones: ZoneDto[] = useMemo(() => {
    return (
      zones?.data.map((zone) => ({
        code: zone.code,
        name: zone.name,
        type: zone.serviceType as unknown as ZoneType,
        cityCode: zone.city.code,
      })) || []
    );
  }, [zones]);

  const data: {
    highwaySections: HighwaySectionDto[];
    cities: CityDto[];
    days: DayOfWeek[];
    zones: ZoneDto[];
  } = useMemo(() => {
    return {
      highwaySections: [],
      cities: mappedCities || [],
      zones: mappedZones,
      days: [
        DayOfWeek.MONDAY,
        DayOfWeek.TUESDAY,
        DayOfWeek.WEDNESDAY,
        DayOfWeek.THURSDAY,
        DayOfWeek.FRIDAY,
        DayOfWeek.SATURDAY,
        DayOfWeek.SUNDAY,
      ],
    };
  }, [mappedCities, mappedZones]);

  return {
    isLoading: isCitiesLoading || isZonesLoading,
    data,
  };
};
